import React, { useState } from 'react';
import {
  Alert,
  Button,
  Content,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { ApiResponseMailDomainAntispamMutation } from '../../../types/api/ApiResponseAntispamPlatform';

interface DisableAntispamModalProps {
  domainId: number;
  platformName: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DisableAntispamModal = ({ domainId, platformName, isOpen, onClose, onSuccess }: DisableAntispamModalProps) => {
  const apiFetch = useApiFetch();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDisable = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch(`/emails/domains/${domainId}/antispam-platform`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });

      let body: ApiResponseMailDomainAntispamMutation | null;
      try {
        body = (await response.json()) as ApiResponseMailDomainAntispamMutation;
      } catch {
        body = null;
      }

      // Antispam endpoints always return a JSend body (errors can arrive at HTTP 200) — branch on status.
      if (response.ok && body?.status === 'success' && body.data?.mail_domain) {
        onSuccess();
        onClose();
        return;
      }
      setError(body?.message ?? `Request failed with status ${response.status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={onClose} onEscapePress={onClose} aria-label="Disable antispam">
      <ModalHeader title="Disable antispam?" titleIconVariant="warning" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Disable failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content>
          {platformName ? (
            <>
              Disable antispam managed by <strong>{platformName}</strong> for this domain?
            </>
          ) : (
            'Disable antispam for this domain?'
          )}{' '}
          It will be removed from the antispam platform&apos;s filtering. This can take a few seconds.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleDisable()} isLoading={isSubmitting} isDisabled={isSubmitting}>
          Disable
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
